import React, { useState, useEffect } from 'react';
import {
  FaArrowLeft, FaUser, FaBuilding, FaBriefcase,
  FaHandHoldingUsd, FaMoneyBillWave, FaFileInvoice, FaPlusCircle
} from 'react-icons/fa';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../../../../utils/api';
import { useAuth } from '../../../../context/AuthContext';
import './SalaryLog.css';

const MONTH_NAMES = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

const SalaryLog = () => {
  const navigate  = useNavigate();
  const { empId } = useParams();
  const { user }  = useAuth();
  const basePath  = user?.role === 'collegeadmin' ? '/collegeadmin' : '/admin';

  const [employee,  setEmployee]  = useState(null);
  const [salaries,  setSalaries]  = useState([]);
  const [advLogs,   setAdvLogs]   = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    if (!empId) return;
    setLoading(true);

    Promise.all([
      api.get('/employees', { params: { limit: 500 } }),
      api.get('/salary',    { params: { employee: empId, limit: 500 } }),
      api.get(`/employees/${empId}/advance-logs`),
    ]).then(([empRes, salRes, advRes]) => {
      const found = (empRes.data.data || []).find(e => String(e._id) === String(empId));
      if (found) setEmployee(found);

      const paid = (salRes.data.data || [])
        .filter(s => s.isPaid)
        .sort((a, b) => new Date(a.paidAt || a.createdAt) - new Date(b.paidAt || b.createdAt));
      setSalaries(paid);

      // only advance-given logs (positive amount, not salary deductions)
      const advances = (advRes.data.data || [])
        .filter(l => l.amount > 0)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      setAdvLogs(advances);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [empId]);

  const payBadgeClass = (type) => {
    const t = (type || '').toLowerCase();
    if (t.includes('cash'))                       return 'cash';
    if (t.includes('bank') || t.includes('net')) return 'bank';
    if (t.includes('upi'))                        return 'upi';
    if (t.includes('cheque'))                     return 'cheque';
    return 'default';
  };

  // Build merged timeline: each salary row + any advance logs that fall BEFORE next salary
  // Advances are shown as special rows between salary rows
  const buildTimeline = () => {
    const rows = [];
    let salIdx = 0;
    let advIdx = 0;
    let slipCounter = 0;

    // We interleave: for each salary, first emit any advances that happened before it
    while (salIdx < salaries.length || advIdx < advLogs.length) {
      const sal = salaries[salIdx];
      const adv = advLogs[advIdx];

      const salDate = sal ? new Date(sal.paidAt || sal.createdAt) : null;
      const advDate = adv ? new Date(adv.date) : null;

      if (advDate && (!salDate || advDate <= salDate)) {
        rows.push({ type: 'advance', data: adv });
        advIdx++;
      } else if (sal) {
        slipCounter++;
        rows.push({ type: 'salary', data: sal, slipNo: slipCounter });
        salIdx++;
      } else {
        break;
      }
    }
    // remaining advances after last salary
    while (advIdx < advLogs.length) {
      rows.push({ type: 'advance', data: advLogs[advIdx] });
      advIdx++;
    }
    return rows;
  };

  const timeline = buildTimeline();

  // Running advance balance & recover security — start from 0
  let runningAdvance = 0;
  let runningRecover = 0;

  if (loading) return (
    <div className="salary-log-of-employee-loader-container">
      <div className="salary-log-of-employee-spinner" />
      <p>Loading Salary Logs...</p>
    </div>
  );

  return (
    <div className="salary-log-of-employee-container">

      {/* Header */}
      <div className="salary-log-of-employee-header">
        <button className="salary-log-of-employee-back-btn" onClick={() => navigate(`${basePath}/employees`)}>
          <FaArrowLeft /> Back
        </button>
        <h2 className="salary-log-of-employee-title">
          {employee?.name ? `${employee.name} — Salary Log` : 'Salary Log'}
        </h2>
      </div>

      {/* Employee Profile Cards */}
      <div className="salary-log-of-employee-profile-section">
        <div className="salary-log-of-employee-college-card">
          <div className="salary-log-of-employee-college-icon"><FaBuilding /></div>
          <div className="salary-log-of-employee-college-text">
            <label>College</label>
            <h3>{employee?.college?.name || '—'}</h3>
          </div>
        </div>

        <div className="salary-log-of-employee-info-grid">
          <div className="salary-log-of-employee-info-card">
            <div className="salary-log-of-employee-icon-wrapper user"><FaUser /></div>
            <div className="salary-log-of-employee-info-text">
              <label>Employee Name</label>
              <span>{employee?.name || '—'}</span>
            </div>
          </div>
          <div className="salary-log-of-employee-info-card">
            <div className="salary-log-of-employee-icon-wrapper briefcase"><FaBriefcase /></div>
            <div className="salary-log-of-employee-info-text">
              <label>Department</label>
              <span>{employee?.department?.name || '—'}</span>
            </div>
          </div>
          <div className="salary-log-of-employee-info-card">
            <div className="salary-log-of-employee-icon-wrapper salary"><FaMoneyBillWave /></div>
            <div className="salary-log-of-employee-info-text">
              <label>Monthly Salary</label>
              <span className="salary-log-monthly">₹ {(employee?.monthlySalary || 0).toLocaleString()}</span>
            </div>
          </div>
          <div className="salary-log-of-employee-info-card highlight">
            <div className="salary-log-of-employee-icon-wrapper advance"><FaHandHoldingUsd /></div>
            <div className="salary-log-of-employee-info-text">
              <label>Current Advance Balance</label>
              <span className="salary-log-of-employee-total-advance">
                ₹ {(employee?.advanceBalance || 0).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="salary-log-of-employee-table-container">
        <div className="salary-log-of-employee-table-header">
          <h3 className="salary-log-of-employee-table-title">
            Salary Payment History
            <span className="salary-log-count">{salaries.length} salary record{salaries.length !== 1 ? 's' : ''}</span>
          </h3>
        </div>

        <div className="salary-log-of-employee-table-wrapper">
          <table className="salary-log-of-employee-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th>Slip No</th>
                <th>Salary Month</th>
                <th>Paid Date</th>
                <th>Reference No</th>
                <th>Total Salary</th>
                <th>Received Salary</th>
                <th>Security Deduction</th>
                <th>Recover Security</th>
                <th>Advance Available</th>
                <th>Advance Adjust</th>
                <th>Payment Mode</th>
                <th>Debited Bank</th>
                <th>Salary Slip</th>
              </tr>
            </thead>
            <tbody>
              {timeline.length === 0 ? (
                <tr>
                  <td colSpan="14" className="salary-log-of-employee-empty">
                    No records found for this employee.
                  </td>
                </tr>
              ) : (() => {
                let sno = 0;
                return timeline.map((row, idx) => {
                  if (row.type === 'advance') {
                    const adv = row.data;
                    runningAdvance += adv.amount;
                    sno++;
                    return (
                      <tr key={`adv-${adv._id}`} className="salary-log-advance-row">
                        <td>{sno}</td>
                        <td>—</td>
                        <td colSpan="3" className="salary-log-advance-label">
                          <span className="salary-log-advance-badge">
                            <FaPlusCircle style={{ marginRight: 5, verticalAlign: 'middle' }} />
                            Advance Added
                          </span>
                          <span className="salary-log-advance-date">
                            {new Date(adv.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </span>
                          {adv.narration && <span className="salary-log-advance-narration"> — {adv.narration}</span>}
                        </td>
                        <td>—</td>
                        <td>—</td>
                        <td>—</td>
                        <td>—</td>
                        <td className="salary-log-adv-given">+ ₹ {adv.amount.toLocaleString()}</td>
                        <td>—</td>
                        <td>
                          <span className={`salary-log-of-employee-pay-badge ${payBadgeClass(adv.paymentType)}`}>
                            {adv.paymentType || '—'}
                          </span>
                        </td>
                        <td className="salary-log-of-employee-ref">
                          {adv.paymentType === 'Cash' 
                            ? '—' 
                            : (adv.bankName && adv.bankAccount
                                ? `${adv.bankName} - ${adv.bankAccount}`
                                : (adv.bank?.name && adv.bank?.accountNumber 
                                    ? `${adv.bank.name} - ${adv.bank.accountNumber}` 
                                    : '—'))
                          }
                        </td>
                        <td>—</td>
                      </tr>
                    );
                  }

                  // salary row
                  const sal = row.data;
                  const advAvailBefore = runningAdvance;
                  runningAdvance -= (parseFloat(sal.advanceAdjust) || 0);
                  const secDed = parseFloat(sal.securityDeduction) || 0;
                  runningRecover += secDed;
                  sno++;

                  return (
                    <tr key={`sal-${sal._id}`} className="salary-log-salary-row">
                      <td>{sno}</td>
                      <td className="salary-log-slip-no">
                        {sal.slipNo || `SLP-${sal.year}-${String(row.slipNo).padStart(3, '0')}`}
                      </td>
                      <td className="salary-log-of-employee-month-text">
                        {MONTH_NAMES[(sal.month || 1) - 1]} {sal.year}
                      </td>
                      <td className="salary-log-of-employee-date">
                        {sal.paidAt
                          ? new Date(sal.paidAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                          : '—'}
                      </td>
                      <td className="salary-log-of-employee-ref">
                        {sal.paymentRef || '—'}
                      </td>
                      <td className="salary-log-monthly-cell">
                        ₹ {(sal.monthlySalary || 0).toLocaleString()}
                      </td>
                      <td className="salary-log-of-employee-received">
                        ₹ {(sal.netSalary || 0).toLocaleString()}
                      </td>
                      <td className="salary-log-sec-ded">
                        {secDed > 0
                          ? <span className="salary-log-sec-ded-val">- ₹ {secDed.toLocaleString()}</span>
                          : <span className="salary-log-nil">—</span>}
                      </td>
                      <td className="salary-log-sec-rec">
                        <span className="salary-log-sec-rec-val">
                          ₹ {runningRecover.toLocaleString()}
                          <span className="salary-log-sec-rec-sep"> / </span>
                          ₹ {(employee?.securityMoney || 0).toLocaleString()}
                        </span>
                        {(employee?.securityMoney || 0) > 0 && (
                          <div className="salary-log-sec-bar-wrap">
                            <div
                              className="salary-log-sec-bar-fill"
                              style={{ width: `${Math.min(100, (runningRecover / employee.securityMoney) * 100)}%` }}
                            />
                          </div>
                        )}
                      </td>
                      <td className="salary-log-of-employee-adv-avail">
                        ₹ {advAvailBefore.toLocaleString()}
                      </td>
                      <td className="salary-log-of-employee-adv-adj">
                        {(sal.advanceAdjust || 0) > 0
                          ? `- ₹ ${Number(sal.advanceAdjust).toLocaleString()}`
                          : '—'}
                      </td>
                      <td>
                        <span className={`salary-log-of-employee-pay-badge ${payBadgeClass(sal.paymentMode)}`}>
                          {sal.paymentMode || '—'}
                        </span>
                      </td>
                      <td className="salary-log-of-employee-ref">
                        {sal.paymentMode === 'Cash' 
                          ? '—' 
                          : (sal.bankName && sal.bankAccount
                              ? `${sal.bankName} - ${sal.bankAccount}`
                              : (sal.bank?.name && sal.bank?.accountNumber 
                                  ? `${sal.bank.name} - ${sal.bank.accountNumber}` 
                                  : '—'))
                        }
                      </td>
                      <td>
                        <button
                          className="salary-log-receipt-btn"
                          title="View Salary Slip"
                          onClick={() => navigate(`${basePath}/salary/receipt`, {
                            state: { salary: sal, collegeName: employee?.college?.name || '', from: 'log', empId }
                          })}
                        >
                          <FaFileInvoice /> Slip
                        </button>
                      </td>
                    </tr>
                  );
                });
              })()}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default SalaryLog;
